import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { TenantContext } from './tenantContext';
import { fetchTenantContext, fetchTenants } from './tenantApi';

const TENANT_STORAGE_KEY = 'ktc_tenant_id';
const REGION_STORAGE_KEY = 'ktc_region_id';

const getStoredValue = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const setStoredValue = (key, value) => {
  try {
    if (value === null || value === undefined || value === '') {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, String(value));
  } catch {
    // no-op
  }
};

export default function TenantProvider({ children }) {
  const [tenantId, setTenantIdState] = useState(() => getStoredValue(TENANT_STORAGE_KEY, 'tenant_default'));
  const [regionId, setRegionIdState] = useState(() => getStoredValue(REGION_STORAGE_KEY, null));
  const [role, setRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const originalFetchRef = useRef(null);

  const applyHeaderInterceptors = useCallback((nextTenantId, nextRegionId) => {
    axios.defaults.headers.common['X-Tenant-ID'] = String(nextTenantId || 'tenant_default');
    if (nextRegionId) {
      axios.defaults.headers.common['X-Region-ID'] = String(nextRegionId);
    } else {
      delete axios.defaults.headers.common['X-Region-ID'];
    }
  }, []);

  const patchFetch = useCallback((nextTenantId, nextRegionId) => {
    if (!originalFetchRef.current) {
      originalFetchRef.current = window.fetch.bind(window);
    }
    const rawFetch = originalFetchRef.current;
    window.fetch = (input, init = {}) => {
      const headers = new Headers(init.headers || {});
      headers.set('X-Tenant-ID', String(nextTenantId || 'tenant_default'));
      if (nextRegionId) {
        headers.set('X-Region-ID', String(nextRegionId));
      }
      return rawFetch(input, {
        ...init,
        headers,
      });
    };
  }, []);

  useEffect(() => {
    setStoredValue(TENANT_STORAGE_KEY, tenantId);
    setStoredValue(REGION_STORAGE_KEY, regionId);
    applyHeaderInterceptors(tenantId, regionId);
    patchFetch(tenantId, regionId);
  }, [tenantId, regionId, applyHeaderInterceptors, patchFetch]);

  const refreshTenantContext = useCallback(async () => {
    try {
      const ctx = await fetchTenantContext();
      setRole(ctx?.role || null);
      setIsSuperAdmin(Boolean(ctx?.isSuperAdmin));
      if (!getStoredValue(TENANT_STORAGE_KEY)) {
        setTenantIdState(String(ctx?.tenantId || 'tenant_default'));
      }
      if (!getStoredValue(REGION_STORAGE_KEY) && ctx?.regionId) {
        setRegionIdState(String(ctx.regionId));
      }
      if (ctx?.isSuperAdmin) {
        const tenantResult = await fetchTenants();
        setAvailableTenants(tenantResult?.items || []);
      }
    } catch {
      setAvailableTenants([]);
    }
  }, []);

  useEffect(() => {
    refreshTenantContext();
  }, [refreshTenantContext]);

  const setTenantId = useCallback((value) => {
    setTenantIdState(String(value || 'tenant_default'));
  }, []);

  const setRegionId = useCallback((value) => {
    setRegionIdState(value ? String(value) : null);
  }, []);

  const contextValue = useMemo(() => ({
    tenantId,
    regionId,
    role,
    isSuperAdmin,
    availableTenants,
    setTenantId,
    setRegionId,
    refreshTenantContext,
  }), [tenantId, regionId, role, isSuperAdmin, availableTenants, setTenantId, setRegionId, refreshTenantContext]);

  return (
    <TenantContext.Provider value={contextValue}>
      {children}
    </TenantContext.Provider>
  );
}

